
import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Interview } from '@/types/interview';
import { toast } from 'sonner';
import { interviewFormSchema, InterviewFormValues } from '../editor/schemas/interviewFormSchema';
import { useFormSubmitUtils } from '@/components/interview/editor/utils/formSubmitUtils';
import { useFormLogger } from '../editor/hooks/useFormLogger';

export const useInterviewForm = (interview: Interview, onClose: () => void) => {
  console.log('[HOOKS VERSION] useInterviewForm - THIS IS THE REAL ONE BEING USED!');
  const [isSaving, setIsSaving] = useState(false);
  const { logFormValues, collectFieldValues, updateInterview } = useFormSubmitUtils();
  
  // On init, log what we're starting with
  useEffect(() => {
    console.log('[useInterviewForm] Initializing with interview:', {
      id: interview.id,
      title: interview.title,
      intervieweeName: interview.intervieweeName,
      hasPhotoUrl: !!interview.photoUrl,
      photoUrl: interview.photoUrl || 'No photo URL',
      hasContextUrl: !!interview.contextUrl,
      contextUrl: interview.contextUrl || 'No context URL',
      timestamp: interview.timestamp
    });
  }, [interview]);
  
  const form = useForm<InterviewFormValues>({
    resolver: zodResolver(interviewFormSchema),
    defaultValues: {
      // Use jobTitle for the "Yrkestittel" field (form calls it "title")
      title: interview.jobTitle || '',
      intervieweeName: interview.intervieweeName,
      company: interview.company || '',
      context: interview.context,
      contextUrl: interview.contextUrl || '',
      question: interview.question,
      answer: interview.answer,
      tags: interview.tags || [],
      photoUrl: interview.photoUrl || '',
      photoUrl2: interview.photoUrl2 || '',
      photographerCredit: interview.photographerCredit || '',
      timestamp: interview.timestamp, // Preserve original timestamp
      isSponsored: interview.isSponsored || false,
      sponsorCompany: interview.sponsorCompany || '',
      sponsorLogoUrl: interview.sponsorLogoUrl || '',
      sponsorLogoPosition: interview.sponsorLogoPosition || 'profile',
    },
    mode: 'onChange', // Track changes immediately
  });

  // Use the form logger
  useFormLogger(form);

  const onSubmit = async (values: InterviewFormValues): Promise<boolean> => {
    try {
      setIsSaving(true);
      
      console.log('[DEBUG] Form onSubmit called with values.tags:', values.tags);
      console.log('[DEBUG] Form current tags field value:', form.getValues('tags'));
      
      logFormValues('useInterviewForm', values);
      
      // ALWAYS treat the form as dirty and requiring updates
      let shouldSave = true;
      
      // Collect all possible sources of photoUrl
      const photoUrlFromValues = values.photoUrl;
      const photoUrlFormValue = form.getValues('photoUrl');
      const finalPhotoUrl = collectFieldValues('photoUrl', photoUrlFromValues, photoUrlFormValue);

      // Collect all possible sources of photoUrl2
      const photoUrl2FromValues = (values as any).photoUrl2;
      const photoUrl2FormValue = (form.getValues('photoUrl2') as any);
      const finalPhotoUrl2 = collectFieldValues('photoUrl2', photoUrl2FromValues, photoUrl2FormValue);
      
      // Collect all possible sources of contextUrl
      const contextUrlFromValues = values.contextUrl;
      const contextUrlFormValue = form.getValues('contextUrl');
      const finalContextUrl = collectFieldValues('contextUrl', contextUrlFromValues, contextUrlFormValue);
      
      console.log('[useInterviewForm] Selected photoUrl for update:', finalPhotoUrl || 'No photo URL');
      console.log('[useInterviewForm] Selected contextUrl for update:', finalContextUrl || 'No context URL');
      
      // CRITICAL: Warn if contextUrl is set in form but won't be included in updateData
      if (finalContextUrl && finalContextUrl.trim() !== '') {
        console.log('[useInterviewForm] ✓ contextUrl will be included in update:', finalContextUrl);
      } else if (contextUrlFormValue && contextUrlFormValue.trim() !== '') {
        console.warn('[useInterviewForm] ⚠️ WARNING: contextUrl is set in form but won\'t be included in updateData!');
        console.warn('[useInterviewForm] Form value:', contextUrlFormValue);
        console.warn('[useInterviewForm] Final value:', finalContextUrl);
      }
      
      // CRITICAL FIX: Don't default to current time, only include timestamp if provided
      let finalTimestamp = values.timestamp;
      if (!finalTimestamp) {
        // Try getting it directly from the form
        const formTimestamp = form.getValues('timestamp'); 
        if (formTimestamp && formTimestamp !== '') {
          finalTimestamp = formTimestamp;
        }
      }
      
      console.log('[useInterviewForm] Timestamp handling:', {
        valuesTimestamp: values.timestamp,
        formTimestamp: form.getValues('timestamp'),
        finalTimestamp: finalTimestamp,
        willInclude: !!finalTimestamp
      });
      
      // Ensure tags is always a proper array with cleaned values
      const formTags = Array.isArray(values.tags) 
        ? values.tags
            .filter(tag => tag !== null && tag !== undefined && typeof tag === 'string')
            .map(tag => {
              // Clean each tag thoroughly
              let cleanTag = String(tag)
                .replace(/^["']|["']$/g, '') // Remove surrounding quotes
                .replace(/["']/g, '') // Remove ANY quotes within the string
                .replace(/^#+/, '') // Remove any leading hashtags
                .replace(/[{}]/g, '') // Remove curly braces
                .replace(/\s+/g, ' ') // Replace multiple spaces with single space
                .replace(/[^\w\sæøåÆØÅ-]/g, '') // Remove special characters except Norwegian letters and hyphens
                .trim();
              return cleanTag;
            })
            .filter(tag => tag.length > 0 && tag !== '{}' && tag !== '"#{}"') // Only keep valid tags
        : [];
      console.log('[useInterviewForm] Cleaned tags array:', formTags);
      console.log('[DEBUG] About to save with these tags:', formTags);

      const updateData: Partial<Interview> = {
        // Form's "title" field is actually the job title (Yrkestittel)
        jobTitle: values.title,
        intervieweeName: values.intervieweeName,
        company: values.company,
        context: values.context,
        contextUrl: finalContextUrl,
        question: values.question,
        answer: values.answer,
        tags: formTags, // Use our properly formatted array
        photoUrl: finalPhotoUrl,
        photoUrl2: finalPhotoUrl2,
        photographerCredit: values.photographerCredit || '',
        isSponsored: values.isSponsored || false,
        sponsorCompany: values.isSponsored ? (values.sponsorCompany || '') : '',
        sponsorLogoUrl: values.isSponsored ? (values.sponsorLogoUrl || '') : '',
        sponsorLogoPosition: values.isSponsored ? (values.sponsorLogoPosition || 'profile') : 'profile',
        // Only include timestamp if we actually have one
        ...(finalTimestamp ? { timestamp: finalTimestamp } : {}),
        // Force updated_at to ensure a database change
        updated_at: new Date().toISOString()
      };
      
      console.log('[useInterviewForm] Final update data:', {
        ...updateData,
        hasPhotoUrl: !!updateData.photoUrl,
        photoUrl: updateData.photoUrl || 'No photo URL',
        hasContextUrl: !!updateData.contextUrl,
        contextUrl: updateData.contextUrl || 'No context URL',
        timestamp: updateData.timestamp,
        updated_at: updateData.updated_at
      });
      
      try {
        const result = await updateInterview(interview.id, updateData);
        if (result) {
          console.log('[useInterviewForm] Interview update successful');
          onClose();
          return true;
        }
        console.error('[useInterviewForm] Interview update failed');
        return false;
      } catch (serviceError) {
        console.error('[useInterviewForm] Error from update service:', serviceError);
        toast.error('Feil ved oppdatering av intervju', { id: 'updating-interview' });
        return false;
      }
    } catch (error) {
      console.error('[useInterviewForm] Error updating interview:', error);
      toast.error('Kunne ikke oppdatere intervju: ' + (error instanceof Error ? error.message : 'Ukjent feil'));
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  return {
    form,
    onSubmit,
    isSaving,
  };
};
